import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, Search, Package, CheckCircle2 } from "lucide-react";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
const SALES_LAST_GROUP_KEY = "salesLastProductGroupName";
const SALES_PENDING_CONFIRM_KEY = "salesPendingConfirmPayload";

function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function malaysiaDateLabelFromDate(d: Date) {
  // Example: 04-Mar-2026
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const label = fmt.format(d); // e.g. "04 Mar 2026"
  return label.replace(/\s+/g, "-");
}

export default function Sales() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [productGroupNames, setProductGroupNames] = useState<string[]>([]);
  const [activeGroupName, setActiveGroupName] = useState<string>("");

  // Date-only selection (GMT+8). Submit is always "today" server-side; reads use selected date.
  const todayUTC = useMemo(() => {
    const key = malaysiaDateKeyFromDate(new Date());
    const [y, m, dd] = key.split("-").map((x) => Number(x));
    return new Date(Date.UTC(y, m - 1, dd));
  }, []);
  const [daysBack, setDaysBack] = useState(0);
  const selectedUTCDate = useMemo(() => {
    const d = new Date(todayUTC);
    d.setUTCDate(d.getUTCDate() - daysBack);
    return d;
  }, [todayUTC, daysBack]);
  const selectedDateKey = useMemo(() => malaysiaDateKeyFromDate(selectedUTCDate), [selectedUTCDate]);
  const isToday = daysBack === 0;

  type SalesProductRow = {
    productId: string;
    name: string;
    sku: string | null;
    qtySold: number;
    sellingPrice: number | null;
    value: number | null;
  };

  type SalesGroupRow = { groupName: string; products: SalesProductRow[] };
  type TeamSalesResponse = {
    team: { id: string; name: string } | null;
    groups: SalesGroupRow[];
    salesSubmission?: {
      isSubmitted: boolean;
      submittedAt: string | null;
    };
  };

  const [salesData, setSalesData] = useState<TeamSalesResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Editable qty sold only for today.
  const [qtySoldByProductId, setQtySoldByProductId] = useState<Record<string, number>>({});
  const [initialQtySoldByProductId, setInitialQtySoldByProductId] = useState<Record<string, number>>({});
  const [focusedPackSoldProductId, setFocusedPackSoldProductId] = useState<string | null>(null);
  const [todaySubmittedViaProof, setTodaySubmittedViaProof] = useState(false);
  const [isResubmitMode, setIsResubmitMode] = useState(false);
  const proceedSectionRef = useRef<HTMLDivElement | null>(null);
  const isSubmittedForSelectedDate = Boolean(salesData?.salesSubmission?.isSubmitted);
  const isSubmittedLocked = (isSubmittedForSelectedDate || (isToday && todaySubmittedViaProof)) && !isResubmitMode;
  const submittedDateLabel = isToday ? "Today" : malaysiaDateLabelFromDate(selectedUTCDate);

  useEffect(() => {
    const loadGroups = async () => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/product-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json: { groups: string[] } = await res.json().catch(() => ({ groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load product groups (${res.status}).`);
          return;
        }

        setProductGroupNames(json.groups ?? []);
        const groups = json.groups ?? [];
        const remembered = localStorage.getItem(SALES_LAST_GROUP_KEY) || "";
        const nextActive = groups.includes(remembered) ? remembered : groups[0] ?? "";
        setActiveGroupName(nextActive);
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadSales = async () => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load sales (${res.status}).`);
          return;
        }

        setSalesData(json);
        setIsResubmitMode(false);

        // initialize edit state for today only
        if (isToday) {
          const nextQty: Record<string, number> = {};
          const nextInitial: Record<string, number> = {};
          for (const g of json.groups ?? []) {
            for (const p of g.products ?? []) {
              nextQty[p.productId] = p.qtySold ?? 0;
              nextInitial[p.productId] = p.qtySold ?? 0;
            }
          }
          setQtySoldByProductId(nextQty);
          setInitialQtySoldByProductId(nextInitial);
        } else {
          setQtySoldByProductId({});
          setInitialQtySoldByProductId({});
        }
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey]);

  useEffect(() => {
    const loadTodaySubmissionProof = async () => {
      if (!token || !isToday) {
        setTodaySubmittedViaProof(false);
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales/bank-proof-today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: any = await res.json().catch(() => ({}));
        const hasBankedAt = Boolean(json?.sales?.bankedAt);
        setTodaySubmittedViaProof(hasBankedAt);
      } catch {
        setTodaySubmittedViaProof(false);
      }
    };

    void loadTodaySubmissionProof();
  }, [apiUrl, token, isToday, selectedDateKey]);

  const activeGroup = useMemo(() => {
    const g = salesData?.groups?.find((x) => x.groupName === activeGroupName);
    return g ?? null;
  }, [salesData, activeGroupName]);

  const filteredProducts = useMemo(() => {
    const list = activeGroup?.products ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.name ?? "").toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q));
  }, [activeGroup, searchQuery]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;

    for (const p of filteredProducts) {
      const qty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
      totalQty += qty;

      if (typeof p.sellingPrice === "number") {
        totalValue += qty * p.sellingPrice;
      }
    }

    return { totalQty, totalValue };
  }, [filteredProducts, qtySoldByProductId]);

  const handleSubmit = async () => {
    if (!isToday || isSubmittedLocked) return;
    setSubmitting(true);
    setSubmitError("");

    try {
      const lines: Array<{ productId: string; qtySold: number }> = Object.entries(qtySoldByProductId).map(
        ([productId, currentQty]) => ({
          productId,
          qtySold: Math.max(0, Math.floor(currentQty)),
        })
      );

      const stagedGroups =
        (salesData?.groups ?? []).map((g) => ({
          groupName: g.groupName,
          products: (g.products ?? []).map((p) => {
            const qty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
            const price = p.sellingPrice ?? null;
            return {
              productId: p.productId,
              name: p.name,
              sku: p.sku,
              qtySold: qty,
              sellingPrice: price,
              value: typeof price === "number" ? qty * price : null,
            };
          }),
        })) ?? [];

      sessionStorage.setItem(
        SALES_PENDING_CONFIRM_KEY,
        JSON.stringify({
          salesDate: selectedDateKey,
          lines,
          groups: stagedGroups,
          requiresReason: isResubmitMode,
        })
      );
      navigate("/app/sales/confirm");
    } catch {
      setSubmitError("Unable to proceed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!activeGroupName) return;
    localStorage.setItem(SALES_LAST_GROUP_KEY, activeGroupName);
  }, [activeGroupName]);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <style>{`
        /* Prevent odd mobile input styling; spinners aren't applicable for type="text". */
        input.no-number-spinners {
          -webkit-appearance: none;
        }
      `}</style>

      {/* Gradient Header */}
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>

            <div className="text-[20px] font-semibold">Sales</div>

            <button
              type="button"
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Sort (disabled for now)"
            >
              <ArrowUpDown size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Date bar */}
      <div className="px-4 pt-1 pb-1">
        <div className="bg-white border border-gray-200 rounded-xl px-2 py-1 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-sm text-slate-700 hover:bg-gray-50"
            onClick={() => setDaysBack((n) => n + 1)}
            aria-label="Go to previous date"
          >
            <span className="font-semibold leading-none">&lt;</span>
          </button>

          <div className="flex-1 flex justify-center px-2">
            <div className="text-center">
              <div className="text-sm font-semibold text-slate-900 truncate leading-4">
                {isToday ? "Today" : malaysiaDateLabelFromDate(selectedUTCDate)}
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
              isToday ? "border-gray-200 bg-white text-sm text-slate-400 cursor-not-allowed" : "border-gray-200 bg-white text-slate-700 hover:bg-gray-50"
            }`}
            disabled={isToday}
            aria-label="Go to next date (toward today)"
            onClick={() => setDaysBack((n) => Math.max(0, n - 1))}
          >
            <span className="font-semibold leading-none">&gt;</span>
          </button>
        </div>
      </div>

      {/* Tab Buttons (ProductGroup.groupName) */}
      <div className="px-4 pt-0 pb-1 bg-gray-50">
        <div className="flex items-center bg-gray-100 rounded-lg p-[4px]">
          <div className="flex w-full">
            {productGroupNames.map((groupName) => {
              const active = groupName === activeGroupName;

              return (
                <button
                  key={groupName}
                  type="button"
                  className={`flex-1 py-2 px-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    active
                      ? "bg-white text-primary shadow-[0_2px_4px_rgba(0,0,0,0.08)]"
                      : "text-gray-600"
                  }`}
                  onClick={() => setActiveGroupName(groupName)}
                >
                  {groupName}
                </button>
              );
            })}

            {loading && productGroupNames.length === 0 ? (
              <div className="py-2 px-3 text-xs text-gray-500 animate-pulse">Loading...</div>
            ) : null}
            {!loading && productGroupNames.length === 0 ? (
              <div className="py-2 px-3 text-xs text-gray-500">No groups</div>
            ) : null}
          </div>
        </div>
      </div>

      {error ? (
        <div className="px-4 pt-2">
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm mb-2">{error}</div>
        </div>
      ) : null}

      {loading && !salesData ? (
        <div className="bg-gray-50 px-4 pt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-white border border-gray-200 animate-pulse mb-2" />
          ))}
        </div>
      ) : null}

      {!loading ? (
        <div className="bg-gray-50 px-4 pt-2 pb-24">
          {isSubmittedLocked ? (
            <div className="mb-1 rounded-lg border border-green-200 bg-green-50 text-green-800 px-3 py-2 text-sm font-medium flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 size={16} className="text-green-700" />
                {`Sales submitted for ${submittedDateLabel.toLowerCase()}`}
              </span>
              {isToday ? (
                <button
                  type="button"
                  className="shrink-0 rounded-md border border-green-300 bg-white px-2.5 py-1 text-xs font-semibold text-green-700"
                  onClick={() => {
                    setIsResubmitMode(true);
                    window.setTimeout(() => {
                      proceedSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 0);
                  }}
                >
                  Resubmit
                </button>
              ) : null}
            </div>
          ) : null}

          {!activeGroupName ? null : (
            <div className="mb-3 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{activeGroupName}</div>
                <div className="text-xs text-slate-500 mt-0.5">{filteredProducts.length} item(s)</div>
              </div>

              <div className="w-[180px] max-w-[50%]">
                <div className="flex items-center bg-white rounded-lg border border-gray-200 h-9 px-2">
                  <Search size={16} className="text-gray-400 mr-1.5 shrink-0" />
                  <input
                    type="text"
                    className="flex-1 outline-none border-0 shadow-none rounded-none p-0 appearance-none text-sm text-slate-900 placeholder:text-gray-400 bg-transparent min-w-0"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {filteredProducts.length ? (
            <div className="pb-4">
              {filteredProducts.map((p) => {
                const currentQty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
                const canEdit = isToday && !isSubmittedLocked;
                const sellingPrice = p.sellingPrice ?? null;
                const value = typeof sellingPrice === "number" ? currentQty * sellingPrice : null;

                return (
                  <div
                    key={p.productId}
                    className="rounded-xl border border-primary/40 shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-2 overflow-hidden bg-white"
                  >
                    <div className="flex flex-row p-3 bg-white">
                      {/* Product image placeholder (mobile has image box) */}
                      <div className="w-[50px] h-[50px] rounded-lg bg-primary/5 border border-primary/30 flex items-center justify-center shrink-0">
                        <Package size={20} className="text-gray-400" />
                      </div>

                      <div className="flex-1 ml-3 mr-3 flex flex-col justify-between min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 leading-5 line-clamp-3">{p.name}</div>
                            {p.sku ? (
                              <div className="mt-1 text-xs text-slate-500">
                                SKU: <span className="text-xs font-medium text-slate-700">{p.sku}</span>
                              </div>
                            ) : null}

                            <div className="mt-2 text-[12px] font-semibold text-slate-900 tabular-nums">
                              {typeof sellingPrice === "number"
                                ? `RM ${sellingPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : "—"}
                            </div>
                          </div>

                          <div className="flex items-start gap-2 ml-auto">
                            <div className="flex flex-col items-center min-w-[60px] shrink-0 gap-1">
                              <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                Pack Sold
                              </div>
                              {canEdit ? (
                                <div className="h-10 w-[42px] rounded-lg border border-primary/40 bg-white flex items-center justify-center px-0">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full text-center text-sm text-slate-900 outline-none border-0 bg-transparent shadow-none rounded-none p-0 tabular-nums no-number-spinners appearance-none"
                                    value={
                                      focusedPackSoldProductId === p.productId && currentQty === 0 ? "" : String(currentQty)
                                    }
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      // Keep digits only; empty means 0 until blur.
                                      const digitsOnly = raw.replace(/[^\d]/g, "");
                                      const parsed = digitsOnly === "" ? 0 : Number(digitsOnly);
                                      const safe = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
                                      setQtySoldByProductId((prev) => ({ ...prev, [p.productId]: safe }));
                                    }}
                                    onFocus={() => {
                                      setFocusedPackSoldProductId(p.productId);
                                    }}
                                    onBlur={() => {
                                      setFocusedPackSoldProductId((id) => (id === p.productId ? null : id));
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="h-10 w-[42px] flex items-center justify-center text-sm font-semibold text-slate-900 tabular-nums">
                                  {currentQty.toLocaleString()}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col items-end text-right gap-1">
                              <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                Value (RM)
                              </div>
                              <div className="h-10 flex items-center justify-end text-sm font-semibold text-slate-900 tabular-nums">
                                {typeof value === "number"
                                  ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Totals bar: keep visible above fixed bottom nav */}
              <div className="fixed bottom-[60px] left-0 right-0 mx-auto w-full max-w-md z-[60] mt-0 bg-green-100/90 backdrop-blur supports-[backdrop-filter]:bg-green-100/80 border-t border-green-300/70 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
                <div className="px-3 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Pack Sold</div>
                    <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">{totals.totalQty.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Value (RM)</div>
                    <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">
                      {`RM ${totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-center py-10">
              <Package size={48} className="text-gray-400" />
              <div className="mt-2 text-sm font-medium text-slate-700">No products found</div>
              {searchQuery ? <div className="mt-1 text-xs text-slate-500">Try adjusting your search</div> : null}
            </div>
          )}

          {isToday ? (
            <div className="mt-5" ref={proceedSectionRef}>
              {submitError ? <div className="mt-2 text-sm text-red-600">{submitError}</div> : null}

              {isResubmitMode ? (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSubmit()}
                    disabled={submitting}
                    className="w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : "Resubmit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsResubmitMode(false)}
                    disabled={submitting}
                    className="w-full rounded-xl border border-gray-300 bg-white text-slate-700 py-3 text-sm font-semibold disabled:opacity-60"
                  >
                    Cancel Resubmit
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={submitting || isSubmittedLocked}
                  className="mt-3 w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Proceed"}
                </button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

